import {
  DocumentEntity,
  DocumentsResultEntity,
  UploadResultEntity,
  DocumentStatus,
} from "../../domain/entities/Document";
import {
  PendingDocumentDTO,
  CompletedDocumentDTO,
  PendingDocumentsResponseDTO,
  CompletedDocumentsResponseDTO,
  UploadResponseDTO,
} from "../models/DocumentDTOs";

/**
 * Document Mapper
 * Maps between API DTOs and Domain Entities
 */
export class DocumentMapper {
  /**
   * Map PendingDocumentDTO to DocumentEntity
   */
  static toPendingDocument(dto: PendingDocumentDTO): DocumentEntity {
    return {
      id: String(dto.id),
      jobId: dto.jobid,
      originalFilename: dto.original_filename,
      filename: dto.filename,
      s3Url: dto.s3_url || "",
      documentType: dto.document_type,
      statementType: dto.statement_type as "bank" | "credit" | undefined,
      status: this.normalizeStatus(dto.status),
      textractStatus: dto.textract_status,
      pushedToBills: String(dto.pushed_to_bills) === "1",
      pushedToStatements: String(dto.pushed_to_statements) === "1",
      stageCompleted: dto.stage_completed,
      isInvoice: dto.is_invoice ? parseInt(String(dto.is_invoice), 10) : undefined,
      duplicateTransactionCount: dto.duplicate_transaction_count,
      processedTransactionCount: dto.processed_transaction_count,
      createdAt: dto.created_at,
      updatedAt: dto.updated_at,
      retriedAt: dto.retried_at,
      retryCount: dto.retry_count !== undefined && dto.retry_count !== null 
        ? (typeof dto.retry_count === 'number' ? dto.retry_count : parseInt(String(dto.retry_count), 10))
        : 0,
      type: dto.type,
      jobType: dto.job_type ?? null,
      jobStatus: dto.job_status ?? null,
      jobErrorMessage: dto.job_error_message ?? null,
      jobCreatedAt: dto.job_created_at ?? null,
      jobUpdatedAt: dto.job_updated_at ?? null,
      jobAgeMinutes: dto.job_age_minutes ?? null,
    };
  }

  /**
   * Map CompletedDocumentDTO to DocumentEntity
   */
  static toCompletedDocument(dto: CompletedDocumentDTO): DocumentEntity {
    return {
      id: String(dto.id),
      jobId: dto.jobid,
      originalFilename: dto.original_filename,
      filename: dto.filename,
      s3Url: dto.s3_url || "",
      documentType: dto.document_type,
      statementType: dto.statement_type as "bank" | "credit" | undefined,
      status: this.normalizeStatus(dto.status),
      textractStatus: dto.textract_status,
      pushedToBills: String(dto.pushed_to_bills) === "1",
      pushedToStatements: String(dto.pushed_to_statements) === "1",
      isInvoice: dto.is_invoice ? parseInt(String(dto.is_invoice), 10) : undefined,
      duplicateTransactionCount: dto.duplicate_transaction_count,
      processedTransactionCount: dto.processed_transaction_count,
      transactionsJson: dto.transactions_json,
      createdAt: dto.created_at,
      updatedAt: dto.updated_at,
      retriedAt: dto.retried_at,
      retryCount: dto.retry_count !== undefined && dto.retry_count !== null 
        ? (typeof dto.retry_count === 'number' ? dto.retry_count : parseInt(String(dto.retry_count), 10))
        : 0,
      type: dto.type,
      jobType: dto.job_type ?? null,
      jobStatus: dto.job_status ?? null,
      jobErrorMessage: dto.job_error_message ?? null,
      jobCreatedAt: dto.job_created_at ?? null,
      jobUpdatedAt: dto.job_updated_at ?? null,
      jobAgeMinutes: dto.job_age_minutes ?? null,
    };
  }

  /**
   * Map PendingDocumentsResponseDTO to DocumentsResultEntity
   */
  static toPendingDocumentsResult(
    dto: PendingDocumentsResponseDTO
  ): DocumentsResultEntity {
    return {
      documents: dto.data.map((doc) => this.toPendingDocument(doc)),
      pagination: {
        total: dto.pagination.total,
        limit: dto.pagination.limit,
        offset: dto.pagination.offset,
        currentPage: dto.pagination.current_page,
        totalPages: dto.pagination.total_pages,
        hasMore: dto.pagination.has_more,
      },
    };
  }

  /**
   * Map CompletedDocumentsResponseDTO to DocumentsResultEntity
   */
  static toCompletedDocumentsResult(
    dto: CompletedDocumentsResponseDTO
  ): DocumentsResultEntity {
    return {
      documents: dto.data.map((doc) => this.toCompletedDocument(doc)),
      pagination: {
        total: dto.pagination.total,
        limit: dto.pagination.limit,
        offset: dto.pagination.offset,
        currentPage: dto.pagination.current_page,
        totalPages: dto.pagination.total_pages,
        hasMore: dto.pagination.has_more,
      },
    };
  }

  /**
   * Map UploadResponseDTO to UploadResultEntity
   */
  static toUploadResult(dto: UploadResponseDTO): UploadResultEntity {
    return {
      success: dto.success,
      message: dto.message,
      jobId: dto.data?.job_id,
      recordId: dto.data?.id,
      reviewFlow: dto.data?.review_flow === true,
    };
  }

  /**
   * Normalize status string to DocumentStatus
   */
  private static normalizeStatus(status: string): DocumentStatus {
    const normalized = status.toLowerCase();
    switch (normalized) {
      case "uploaded":
        return "uploaded";
      case "processing":
        return "processing";
      case "completed":
        return "completed";
      case "failed":
        return "failed";
      case "awaiting_approval":
        return "awaiting_approval";
      case "rejected":
        return "rejected";
      default:
        return "processing";
    }
  }
}
